import { useEffect, useMemo, useState } from "react";
import { Rocket, X, Clock, MapPin, Calendar, Trash2, Check, Repeat as RepeatIcon, Flame, Power } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import api, { errorMessage } from "@/lib/api";

const stateBadge = {
  live:     { className: "bg-emerald-500/25 text-emerald-300 border-emerald-500/40", label: "LIVE" },
  upcoming: { className: "bg-orange-500/20 text-orange-300 border-orange-500/40", label: "UPCOMING" },
  ended:    { className: "bg-slate-700/50 text-slate-400 border-slate-600", label: "ENDED" },
  cancelled:{ className: "bg-red-500/20 text-red-300 border-red-500/40", label: "CANCELLED" },
};

// iOS-style weekday buttons — index 0 = Sunday.
const DAYS = [
  { token: "Sun", letter: "S" },
  { token: "Mon", letter: "M" },
  { token: "Tue", letter: "T" },
  { token: "Wed", letter: "W" },
  { token: "Thu", letter: "T" },
  { token: "Fri", letter: "F" },
  { token: "Sat", letter: "S" },
];
const WEEKDAY_TOKENS = ["Mon", "Tue", "Wed", "Thu", "Fri"];
const WEEKEND_TOKENS = ["Sat", "Sun"];

function repeatSubtitle(tokens) {
  const s = new Set(tokens);
  if (s.size === 0) return "Never";
  if (s.size === 7) return "Every day";
  if (WEEKDAY_TOKENS.every(t => s.has(t)) && s.size === 5) return "Weekdays";
  if (WEEKEND_TOKENS.every(t => s.has(t)) && s.size === 2) return "Weekends";
  const order = ["Sun","Mon","Tue","Wed","Thu","Fri","Sat"];
  return "Custom · " + order.filter(t => s.has(t)).join(", ");
}

export default function BoostModal({ open, onClose, districts, onChange }) {
  // Always-On (V1 flow)
  const [alwaysOn, setAlwaysOn] = useState(null); // null=loading, true/false=known
  const [alwaysOnDistricts, setAlwaysOnDistricts] = useState([]);
  const [alwaysOnTotal, setAlwaysOnTotal] = useState(0);
  const [togglingAO, setTogglingAO] = useState(false);
  // Schedule controls (legacy — only shown when Always-On is OFF)
  const [selectedDistricts, setSelectedDistricts] = useState(() => new Set());
  const [start, setStart] = useState("09:00");
  const [end, setEnd] = useState("10:00");
  const [dateKind, setDateKind] = useState("today");
  const [repeatDays, setRepeatDays] = useState([]);
  const [busy, setBusy] = useState(false);
  const [boosts, setBoosts] = useState([]);

  const loadList = async () => {
    try { const r = await api.get("/admin/boost/list"); setBoosts(r.data.boosts || []); }
    catch { /* silent */ }
  };
  const loadAlwaysOn = async () => {
    try {
      const r = await api.get("/admin/boost/always-on");
      setAlwaysOn(!!r.data.always_on);
      setAlwaysOnDistricts(r.data.districts_live || []);
      setAlwaysOnTotal(r.data.total_districts || 0);
    } catch {
      setAlwaysOn(false);
    }
  };
  useEffect(() => {
    if (open) {
      loadAlwaysOn();
      loadList();
      setSelectedDistricts(new Set());
      setRepeatDays([]);
    }
  }, [open]);

  const enableAlwaysOn = async () => {
    setTogglingAO(true);
    try {
      const r = await api.post("/admin/boost/always-on");
      toast.success(`Always ON · ${r.data.created} districts activated${r.data.already_on ? ` (${r.data.already_on} already on)` : ""}`);
      await Promise.all([loadAlwaysOn(), loadList()]);
      onChange && onChange();
    } catch (e) { toast.error(errorMessage(e)); }
    finally { setTogglingAO(false); }
  };
  const disableAlwaysOn = async () => {
    if (!window.confirm("Turn Always-On OFF? Bank staff will only see cases inside scheduled boost windows.")) return;
    setTogglingAO(true);
    try {
      const r = await api.post("/admin/boost/always-off");
      toast.success(`Always OFF · ${r.data.disabled} district${r.data.disabled === 1 ? "" : "s"} stopped`);
      await Promise.all([loadAlwaysOn(), loadList()]);
      onChange && onChange();
    } catch (e) { toast.error(errorMessage(e)); }
    finally { setTogglingAO(false); }
  };

  const allSelected = districts.length > 0 && selectedDistricts.size === districts.length;
  const toggleDistrict = (d) => setSelectedDistricts(prev => {
    const next = new Set(prev);
    if (next.has(d)) next.delete(d); else next.add(d);
    return next;
  });
  const toggleSelectAll = () => setSelectedDistricts(allSelected ? new Set() : new Set(districts));
  const toggleDay = (tok) => setRepeatDays(prev => prev.includes(tok) ? prev.filter(t => t !== tok) : [...prev, tok]);
  const setRepeatPreset = (kind) => {
    if (kind === "everyday") setRepeatDays(["Sun","Mon","Tue","Wed","Thu","Fri","Sat"]);
    else if (kind === "weekdays") setRepeatDays([...WEEKDAY_TOKENS]);
    else if (kind === "weekends") setRepeatDays([...WEEKEND_TOKENS]);
    else setRepeatDays([]);
  };

  const activate = async () => {
    const list = Array.from(selectedDistricts);
    if (list.length === 0) return toast.error("Select at least one district");
    if (!start || !end) return toast.error("Enter start & end time");
    setBusy(true);
    try {
      const r = await api.post("/admin/boost/activate", {
        districts: list, start_time: start, end_time: end,
        date: dateKind, repeat_days: repeatDays,
      });
      const created = r.data.created?.length || 0;
      const skipped = r.data.skipped?.length || 0;
      toast.success(`Boost activated · ${created} scheduled${skipped ? ` · ${skipped} skipped` : ""}`);
      await loadList();
      onChange && onChange();
    } catch (e) { toast.error(errorMessage(e)); }
    finally { setBusy(false); }
  };

  const cancel = async (id) => {
    if (!window.confirm("Cancel this boost? Bank staff will lose access.")) return;
    try {
      await api.delete(`/admin/boost/${id}`);
      toast.success("Boost cancelled");
      await loadList();
      onChange && onChange();
    } catch (e) { toast.error(errorMessage(e)); }
  };

  const subtitle = repeatSubtitle(repeatDays);
  const previewChips = useMemo(() => {
    const arr = Array.from(selectedDistricts);
    if (arr.length <= 2) return arr;
    return [...arr.slice(0, 2), `+${arr.length - 2} more`];
  }, [selectedDistricts]);

  // Scheduled boosts to show: hide always_on rows from the legacy list to
  // avoid noise (they're represented by the Always-On status card instead).
  const scheduled = boosts.filter(b => !b.always_on);

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-slate-900 border-orange-500/30 text-white max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="boost-modal">
        <DialogHeader>
          <DialogTitle className="font-heading text-2xl flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-orange-500/20 border border-orange-500/40 flex items-center justify-center">
              <Rocket className="w-5 h-5 text-orange-400"/>
            </div>
            <div>
              <div className="text-orange-400">Boost District</div>
              <div className="text-xs text-slate-500 font-normal uppercase tracking-widest">V1 · Always-On mode</div>
            </div>
          </DialogTitle>
        </DialogHeader>

        {/* ---------- V1 Always-On block ---------- */}
        <div className="mt-2" data-testid="always-on-section">
          {alwaysOn ? (
            <div className="rounded-xl bg-emerald-500/10 border border-emerald-500/40 p-4 space-y-3" data-testid="always-on-live-card">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-emerald-500/20 border border-emerald-500/50 flex items-center justify-center animate-pulse">
                  <Flame className="w-5 h-5 text-emerald-300"/>
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-emerald-300 font-heading text-lg">🚀 BOOST · ALWAYS ON</div>
                  <div className="text-[11px] text-emerald-200/80">All {alwaysOnDistricts.length}/{alwaysOnTotal} districts LIVE · Every bank staff sees files instantly</div>
                </div>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={disableAlwaysOn}
                  disabled={togglingAO}
                  data-testid="always-off-btn"
                  className="border-red-500/50 text-red-300 hover:bg-red-500/10 rounded-full">
                  <Power className="w-3.5 h-3.5 mr-1.5"/> Turn OFF
                </Button>
              </div>
              <div className="flex flex-wrap gap-1.5 max-h-32 overflow-y-auto" data-testid="always-on-districts">
                {alwaysOnDistricts.map(d => (
                  <Badge key={d} className="bg-emerald-500/15 text-emerald-200 border-emerald-500/40 text-[10px] flex items-center gap-1">
                    <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse"/> {d} · LIVE
                  </Badge>
                ))}
              </div>
            </div>
          ) : (
            <button
              type="button"
              onClick={enableAlwaysOn}
              disabled={togglingAO || alwaysOn === null}
              data-testid="always-on-btn"
              className="w-full rounded-xl bg-gradient-to-r from-emerald-500 to-emerald-400 hover:from-emerald-400 hover:to-emerald-300 text-slate-900 font-black text-base py-4 flex items-center justify-center gap-2 shadow-lg shadow-emerald-500/30 transition disabled:opacity-50">
              <Flame className="w-5 h-5"/> 🔥 ALWAYS ON FOR ALL DISTRICTS
            </button>
          )}
        </div>

        {/* ---------- Legacy scheduling controls (hidden in Always-On) ---------- */}
        {!alwaysOn && alwaysOn !== null && (
          <div className="grid gap-4 mt-4" data-testid="legacy-schedule-section">
            <div className="text-[10px] uppercase tracking-widest text-slate-500 border-t border-slate-800 pt-3">
              Or schedule a fixed window
            </div>
            <div>
              <div className="flex items-center justify-between mb-1">
                <div className="text-[10px] uppercase tracking-widest text-slate-500 flex items-center gap-1">
                  <MapPin className="w-3 h-3"/> Districts
                  <span className="ml-1 normal-case tracking-normal text-slate-400 font-mono-alt">({selectedDistricts.size}/{districts.length})</span>
                </div>
                <button type="button" onClick={toggleSelectAll} data-testid="boost-select-all"
                  className="text-[11px] uppercase tracking-widest text-orange-400 hover:text-orange-300 font-bold">
                  {allSelected ? "Deselect All" : "Select All"}
                </button>
              </div>
              {selectedDistricts.size > 0 && (
                <div className="flex flex-wrap gap-1.5 mb-2" data-testid="boost-selected-chips">
                  {previewChips.map((c, i) => (
                    <Badge key={i} className="bg-orange-500/15 text-orange-200 border-orange-500/40 text-[10px]">{c}</Badge>
                  ))}
                </div>
              )}
              <div data-testid="boost-districts-list"
                className="max-h-40 overflow-y-auto rounded-md border border-slate-700 bg-slate-950 divide-y divide-slate-800">
                {districts.length === 0 && (
                  <div className="text-center text-slate-500 text-xs py-6">No districts in Location Master.</div>
                )}
                {districts.map(d => {
                  const on = selectedDistricts.has(d);
                  return (
                    <button key={d} type="button" onClick={() => toggleDistrict(d)}
                      data-testid={`boost-district-${d}`}
                      className={`w-full flex items-center gap-2 px-3 py-2 text-sm text-left hover:bg-white/5 transition ${on ? "bg-orange-500/5" : ""}`}>
                      <span className={`w-4 h-4 rounded border flex items-center justify-center shrink-0 ${on ? "bg-orange-500 border-orange-400" : "border-slate-600"}`}>
                        {on && <Check className="w-3 h-3 text-slate-900"/>}
                      </span>
                      <span className={on ? "text-white font-bold" : "text-slate-300"}>{d}</span>
                    </button>
                  );
                })}
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <div className="text-[10px] uppercase tracking-widest text-slate-500 mb-1 flex items-center gap-1"><Clock className="w-3 h-3"/>Start (IST)</div>
                <Input data-testid="boost-start" type="time" value={start} onChange={e => setStart(e.target.value)}
                  className="bg-slate-950 border-slate-700 focus:border-orange-500"/>
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-widest text-slate-500 mb-1 flex items-center gap-1"><Clock className="w-3 h-3"/>End (IST)</div>
                <Input data-testid="boost-end" type="time" value={end} onChange={e => setEnd(e.target.value)}
                  className="bg-slate-950 border-slate-700 focus:border-orange-500"/>
              </div>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-widest text-slate-500 mb-1 flex items-center gap-1"><Calendar className="w-3 h-3"/>Date</div>
              <div className="grid grid-cols-2 gap-2">
                {["today","tomorrow"].map(k => (
                  <button key={k} data-testid={`boost-date-${k}`} type="button" onClick={() => setDateKind(k)}
                    className={`rounded-md py-2 text-sm font-bold uppercase tracking-widest border transition ${dateKind===k?"bg-orange-500/20 border-orange-500 text-orange-300":"bg-slate-950 border-slate-700 text-slate-400 hover:border-slate-500"}`}>
                    {k}
                  </button>
                ))}
              </div>
            </div>
            <div className="rounded-lg bg-slate-950/60 border border-slate-800 p-3" data-testid="boost-repeat-section">
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                  <RepeatIcon className="w-3.5 h-3.5 text-orange-400"/>
                  <div>
                    <div className="text-sm font-bold text-white">Repeat</div>
                    <div className="text-[11px] text-slate-500" data-testid="boost-repeat-subtitle">{subtitle}</div>
                  </div>
                </div>
                <div className="flex gap-1">
                  {[["Off",""],["Wkdys","weekdays"],["Wknds","weekends"],["Every","everyday"]].map(([lbl, k]) => (
                    <button key={lbl} type="button" onClick={() => setRepeatPreset(k)}
                      data-testid={`boost-repeat-preset-${k || "off"}`}
                      className="text-[10px] uppercase tracking-widest px-2 py-1 rounded-full border border-slate-700 text-slate-400 hover:border-orange-500 hover:text-orange-300 transition">{lbl}</button>
                  ))}
                </div>
              </div>
              <div className="flex justify-between gap-1.5 mt-1">
                {DAYS.map((d, i) => {
                  const on = repeatDays.includes(d.token);
                  return (
                    <button key={i} type="button" onClick={() => toggleDay(d.token)}
                      data-testid={`boost-day-${d.token}`}
                      className={`w-10 h-10 rounded-full text-sm font-bold border transition active:scale-95 ${
                        on ? "bg-[#0A84FF] border-[#0A84FF] text-white shadow-lg shadow-[#0A84FF]/30"
                           : "bg-slate-800 border-slate-700 text-slate-400 hover:border-slate-500"
                      }`}>{d.letter}</button>
                  );
                })}
              </div>
            </div>
            <Button data-testid="boost-activate" onClick={activate} disabled={busy || selectedDistricts.size === 0}
              className="bg-orange-500 hover:bg-orange-400 text-slate-950 font-bold text-base py-6 rounded-full disabled:opacity-50">
              <Rocket className="w-5 h-5 mr-2"/> {busy ? "Activating…" : `Activate Boost${selectedDistricts.size > 1 ? ` × ${selectedDistricts.size} districts` : ""}`}
            </Button>
          </div>
        )}

        {/* ---------- Scheduled boosts list (only non-always-on) ---------- */}
        {scheduled.length > 0 && (
          <div className="mt-6">
            <div className="text-[10px] uppercase tracking-widest text-slate-500 mb-2">Scheduled Boosts</div>
            <div className="space-y-2">
              {scheduled.map(b => {
                const sb = stateBadge[b.state] || stateBadge.upcoming;
                return (
                  <div key={b.id} data-testid={`boost-item-${b.id}`} className="flex items-center justify-between bg-slate-950/60 border border-white/5 rounded-lg px-3 py-2">
                    <div className="flex items-center gap-3 min-w-0">
                      <Badge className={sb.className + " text-[9px] uppercase tracking-widest whitespace-nowrap"}>{sb.label}</Badge>
                      <div className="text-sm min-w-0">
                        <div className="text-white font-bold truncate">{b.district}</div>
                        <div className="text-[11px] text-slate-500 font-mono-alt">
                          {b.repeat_label ? <span className="text-orange-300 mr-1">{b.repeat_label} · </span> : null}
                          {b.date_label} · {b.start_time} – {b.end_time} IST
                        </div>
                      </div>
                    </div>
                    <Button data-testid={`boost-cancel-${b.id}`} size="sm" variant="ghost" onClick={() => cancel(b.id)}
                      className="text-red-400 hover:text-red-300 hover:bg-red-500/10">
                      <Trash2 className="w-4 h-4"/>
                    </Button>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        <div className="flex justify-end mt-4">
          <Button data-testid="boost-close" variant="ghost" onClick={onClose} className="text-slate-300 hover:text-white">
            <X className="w-4 h-4 mr-2"/> Close
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
